/**
 * @file Settings page — editable resale formula coefficients (laptops + phones)
 * and a read-only display of the hardcoded admin allowlist.
 *
 * The formula editor saves to /config/resaleFormulas. The offboarding pages
 * read it on each render so changes propagate without a deploy.
 */

import { el, clear, formatDate, toast } from '../utils.js';
import { Icons } from '../icons.js';
import { STRV_DOMAIN_RE } from '../config.js';
import {
  getConfig, setConfig, watchConfig,
  watchAdmins, addAdmin, removeAdmin,
} from '../db.js';
import { defaultFormulas } from '../formula.js';
import { renderSyncButton } from '../sync-button.js';
import { renderPageHead } from '../page-header.js';
import { syncBambooAssets, testSlack } from '../api.js';
import { SYNC_STAGES, buildSyncAuditDetails, runStage, runExclusive, isSyncBusy, onSyncBusyChange, SyncBusyError } from '../lib/sync-stages.js';
import { isTestMode } from '../test-mode.js';
import { getCurrentUser } from '../auth.js';
import { logAuditEvent }  from '../audit-log.js';
import { AUDIT_ACTION }   from '../lib/audit-actions.js';

export async function renderSettings(host) {
  clear(host);

  host.appendChild(renderPageHead('Settings', 'Editable runtime configuration.'));

  // ── Sync panel (top of page) ──────────────────────────────────────────
  host.appendChild(renderSyncCard(host));

  // ── Slack notifications ──────────────────────────────────────────────
  host.appendChild(renderSlackCard(host));

  const formulas = (await getConfig('resaleFormulas')) || defaultFormulas();

  // ── Resale formula editor ───────────────────────────────────────────────
  const formulaCard = el('section', { class: 'card' },
    el('h2', { class: 'heading' }, 'Resale value formula'),
    el('p', { class: 'muted' },
      'Used during offboarding to suggest a buy-back price. For the first "grace" months the device is valued at full price (STRV doesn\'t sell it yet); at the end of grace the value drops to the given % of purchase price, then falls linearly to the residual floor by the end of the depreciation period. Grace 0 at 100% = plain linear (laptops). Per-model overrides match when every word of the override appears somewhere in the device name (any order, case/diacritics-insensitive). The first matching row wins, so order specific rules above generic ones — use ↑/↓ to reorder.'),
  );

  const renderFormulaSection = (key) => {
    const f = formulas[key] || defaultFormulas()[key];
    const monthsInput   = el('input', { type: 'number', min: '1', value: String(f.depreciationMonths) });
    const residualInput = el('input', { type: 'number', min: '0', value: String(f.residualValue) });
    // Grace + cliff (the phones model — see src/formula.js). Grace 0 with
    // 100% = the plain laptop line, so both categories share one editor.
    const graceInput    = el('input', { type: 'number', min: '0', value: String(f.gracePeriodMonths ?? 0) });
    const sellableInput = el('input', { type: 'number', min: '1', max: '100', value: String(f.sellableAtPercent ?? 100) });
    const overridesHost = el('div', { class: 'stack', style: 'gap:8px' });

    const renderOverrides = (list) => {
      clear(overridesHost);
      list.forEach((row, i) => overridesHost.appendChild(renderOverrideRow(row, i, list, () => renderOverrides(list))));
      overridesHost.appendChild(el('button', {
        class: 'btn-sm btn-outline btn',
        style: 'align-self:flex-start',
        onClick: () => {
          list.push({ match: '', residualValue: f.residualValue });
          renderOverrides(list);
        },
      }, Icons.plus(), 'Add override'));
    };
    renderOverrides([...(f.modelOverrides || [])]);

    const save = el('button', {
      class: 'btn',
      onClick: async () => {
        const before = formulas[key] || {};
        const after = {
          label: f.label,
          depreciationMonths: Number(monthsInput.value) || f.depreciationMonths,
          residualValue:      Number(residualInput.value) || f.residualValue,
          gracePeriodMonths:  Math.max(0, Number(graceInput.value) || 0),
          sellableAtPercent:  Math.min(100, Math.max(1, Number(sellableInput.value) || 100)),
          modelOverrides:     extractOverrides(overridesHost),
        };
        if (after.gracePeriodMonths >= after.depreciationMonths) {
          toast('Grace period must be shorter than the depreciation period.', 'error');
          return;
        }
        try {
          await setConfig('resaleFormulas', { ...formulas, [key]: after });
          // Audit: snapshot the BEFORE + AFTER values so we can see how
          // the formula evolved and replay the change if needed.
          logAuditEvent({
            action: AUDIT_ACTION.SYSTEM_CONFIG_CHANGE,
            target: { type: 'config', id: `resaleFormulas/${key}`, label: `${f.label} resale formula` },
            details: {
              path:   `resaleFormulas.${key}`,
              before: { depreciationMonths: before.depreciationMonths ?? null, residualValue: before.residualValue ?? null, gracePeriodMonths: before.gracePeriodMonths ?? null, sellableAtPercent: before.sellableAtPercent ?? null, modelOverrides: before.modelOverrides || [] },
              after:  { depreciationMonths: after.depreciationMonths,            residualValue: after.residualValue,            gracePeriodMonths: after.gracePeriodMonths,          sellableAtPercent: after.sellableAtPercent,          modelOverrides: after.modelOverrides },
            },
          });
          toast(`${f.label} formula saved.`, 'success');
        } catch (err) {
          console.error(err);
          toast(err.message || 'Save failed.', 'error');
        }
      },
    }, 'Save');

    return el('div', { class: 'stack', style: 'margin-top:24px' },
      el('h3', null, f.label),
      el('div', { class: 'grid grid-2' },
        el('div', { class: 'field' }, el('label', null, 'Depreciation months'),  monthsInput),
        el('div', { class: 'field' }, el('label', null, 'Residual value (CZK)'), residualInput),
        el('div', { class: 'field' }, el('label', null, 'Grace months (full price, not sold)'), graceInput),
        el('div', { class: 'field' }, el('label', null, 'Value after grace (% of price)'), sellableInput),
      ),
      el('div', { class: 'field' }, el('label', null, 'Per-model overrides'), overridesHost),
      el('div', { class: 'row', style: 'justify-content:flex-end' }, save),
    );
  };

  formulaCard.appendChild(renderFormulaSection('laptop'));
  formulaCard.appendChild(el('div', { class: 'divider' }));
  formulaCard.appendChild(renderFormulaSection('phone'));
  host.appendChild(formulaCard);

  host.appendChild(renderAdminsCard(host));
}

// ── Admins card (live editable) ────────────────────────────────────────────
//
// Reads /admins via watchAdmins. Add field accepts an @strv.com email and
// writes /admins/{lowercased}. Remove buttons delete the row, except on
// your own row (Firestore rule enforces it too, so an admin can't lock the
// last admin out by removing themselves).
function renderAdminsCard(host) {
  const card = el('section', { class: 'card', style: 'margin-top:24px' },
    el('h2', { class: 'heading' }, 'Admins'),
    el('p', { class: 'muted' },
      'Add or remove admin privileges. Changes apply within a second — no redeploy needed. Demoting yourself isn\'t allowed; another admin has to do it.'),
  );

  const listHost = el('ul', { style: 'margin-top:16px;list-style:none;padding:0;display:flex;flex-direction:column;gap:8px' });
  const skeleton = el('p', { class: 'muted' }, 'Loading…');
  listHost.appendChild(skeleton);

  // Add-admin row.
  const emailInput = el('input', { type: 'email', placeholder: 'name@strv.com' });
  const nameInput  = el('input', { type: 'text',  placeholder: 'Display name (optional)' });
  const addButton  = el('button', { class: 'btn' }, Icons.plus(), 'Add admin');
  addButton.addEventListener('click', async () => {
    const email = emailInput.value.trim().toLowerCase();
    const displayName = nameInput.value.trim() || null;
    if (!STRV_DOMAIN_RE.test(email)) {
      toast('Email must be a @strv.com address.', 'error');
      return;
    }
    addButton.disabled = true;
    try {
      const actor = getCurrentUser()?.email || null;
      await addAdmin({ email, displayName, addedBy: actor });
      logAuditEvent({
        action: AUDIT_ACTION.ADMIN_ADD,
        target: { type: 'admin', id: email, label: displayName || email },
        details: { email, displayName },
      });
      toast(`${email} is now an admin.`, 'success');
      emailInput.value = '';
      nameInput.value = '';
    } catch (err) {
      console.error('[settings] addAdmin', err);
      toast(err.code === 'permission-denied'
        ? 'Permission denied — only admins can add admins.'
        : (err.message || 'Could not add admin.'), 'error');
    } finally {
      addButton.disabled = false;
    }
  });

  card.appendChild(listHost);
  card.appendChild(el('div', { class: 'divider', style: 'margin:16px 0' }));
  card.appendChild(el('div', { class: 'stack', style: 'gap:8px' },
    el('div', { class: 'label' }, 'Add admin'),
    el('div', { class: 'row', style: 'gap:8px;align-items:stretch' },
      el('div', { style: 'flex:1' }, emailInput),
      el('div', { style: 'flex:1' }, nameInput),
      addButton,
    ),
  ));

  const unsub = watchAdmins((admins) => {
    clear(listHost);
    if (!admins.length) {
      listHost.appendChild(el('p', { class: 'muted' }, 'No admins on file. (Someone has to be — check the seed script.)'));
      return;
    }
    const me = getCurrentUser()?.email?.toLowerCase() || '';
    const sorted = [...admins].sort((a, b) => (a.email || '').localeCompare(b.email || ''));
    for (const a of sorted) {
      const isSelf = a.email === me;
      listHost.appendChild(el('li', { class: 'row', style: 'gap:12px;padding:8px 0;border-bottom:1px solid var(--border)' },
        el('span', { class: 'badge badge-done' }, isSelf ? 'You' : 'Admin'),
        el('div', { style: 'flex:1' },
          el('div', null, a.displayName || a.email),
          el('div', { class: 'muted', style: 'font-size:var(--fs-small)' },
            `${a.email}${a.addedAt ? ` · added ${formatDate(a.addedAt)}` : ''}${a.addedBy && a.addedBy !== 'bootstrap' ? ` by ${a.addedBy}` : ''}`,
          ),
        ),
        el('button', {
          class: 'btn-ghost btn btn-sm',
          disabled: isSelf || null,
          title: isSelf ? 'You can\'t remove yourself — ask another admin' : 'Remove admin',
          onClick: async () => {
            if (!window.confirm(`Remove admin privileges from ${a.email}?`)) return;
            try {
              const actor = getCurrentUser()?.email || null;
              await removeAdmin(a.email);
              logAuditEvent({
                action: AUDIT_ACTION.ADMIN_REMOVE,
                target: { type: 'admin', id: a.email, label: a.displayName || a.email },
                details: { email: a.email, removedBy: actor },
              });
              toast(`${a.email} is no longer an admin.`, 'success');
            } catch (err) {
              console.error('[settings] removeAdmin', err);
              toast(err.message || 'Could not remove admin.', 'error');
            }
          },
        }, 'Remove'),
      ));
    }
  });
  host.addEventListener('cleanup', unsub, { once: true }); // fires on #page, not the card

  return card;
}

function renderOverrideRow(row, index, list, onChange) {
  const matchInput    = el('input', { type: 'text',  value: row.match || '',           placeholder: 'e.g. macbook air M1' });
  const residualInput = el('input', { type: 'number', value: String(row.residualValue || 0) });
  matchInput.addEventListener('input', () => { row.match = matchInput.value; });
  residualInput.addEventListener('input', () => { row.residualValue = Number(residualInput.value) || 0; });

  // Order IS precedence (first matching row wins), so rows can be moved.
  // Swap in the backing list and re-render; extractOverrides reads DOM order.
  const move = (delta) => {
    const j = index + delta;
    if (j < 0 || j >= list.length) return;
    [list[index], list[j]] = [list[j], list[index]];
    onChange();
  };
  const upBtn   = el('button', { class: 'btn-ghost btn btn-sm', title: 'Move up (higher precedence)',  onClick: () => move(-1) }, '↑');
  const downBtn = el('button', { class: 'btn-ghost btn btn-sm', title: 'Move down (lower precedence)', onClick: () => move(1) }, '↓');
  if (index === 0) upBtn.disabled = true;
  if (index === list.length - 1) downBtn.disabled = true;

  return el('div', { class: 'row', dataset: { row: 'true' } },
    el('span', { class: 'muted', style: 'width:22px;text-align:right;font-size:var(--fs-small)' }, String(index + 1)),
    el('div', { style: 'flex:1' }, matchInput),
    el('div', { style: 'flex:0 0 160px' }, residualInput),
    upBtn, downBtn,
    el('button', {
      class: 'btn-ghost btn btn-sm',
      title: 'Remove',
      onClick: () => { list.splice(index, 1); onChange(); },
    }, Icons.close()),
  );
}

function extractOverrides(host) {
  const rows = host.querySelectorAll('[data-row="true"]');
  const out = [];
  rows.forEach((row) => {
    const inputs = row.querySelectorAll('input');
    const match = inputs[0].value.trim();
    const residual = Number(inputs[1].value);
    if (match && Number.isFinite(residual)) out.push({ match, residualValue: residual });
  });
  return out;
}

// ── Sync card ──────────────────────────────────────────────────────────
//
// Two buttons here:
//   • Sync everything — runs the full pipeline in order:
//       1. BambooHR people list (one custom-report call)
//       2. Abra Flexi devices (paginated /majetek)
//       3. Meraki MDM device inventory
//       4. QA "Testing devices" Google Sheet (every tab)
//       5. Materialise — joins the four raw mirrors into /devices, flags
//          cross-system mismatches (Meraki vs. Abra, Testing sheet vs. Abra)
//       6. Joining-form Google Sheet — matches new-hire submissions to
//          BambooHR employees, materialises ordered items into onboarding.
//     The nightly cron at 02:00 Europe/Prague does the exact same thing.
//   • Sync phone tariffs — per-employee BambooHR customAssets fan-out. This
//     is the only path that updates phone-tariff state on /me. Kept
//     separate because it's an expensive ~300-call burst we only want when
//     it's actually needed.
function renderSyncCard(host) {
  const card = el('section', { class: 'card', style: 'margin-bottom:24px' },
    el('h2', { class: 'heading' }, 'Sync'),
    el('p', { class: 'muted' },
      "Gear mirrors BambooHR (people), Abra Flexi (devices), Meraki MDM, IRU MDM, and the QA testing-devices Google Sheet nightly at 02:00 Europe/Prague. Joining-form responses are pulled in the same run. Phone tariffs (Bamboo customAssets) refresh only when you ask — that's the expensive per-employee fan-out."),
  );

  // ── Status lines — one per registry stage that reports to /config/sync ──
  const statusEls = new Map(); // stage.key → <p>
  const statusStack = el('div', { class: 'stack', style: 'gap:4px;margin-bottom:12px' });
  for (const stage of SYNC_STAGES) {
    if (!stage.status) continue; // e.g. the testing sheet writes no status
    const line = el('p', { class: 'muted', style: 'font-size:var(--fs-small);margin:0' },
      `${stage.status.label} — loading status...`);
    statusEls.set(stage.key, line);
    statusStack.appendChild(line);
  }
  const assetsStatus = el('p', { class: 'muted', style: 'font-size:var(--fs-small);margin:0' }, 'Phone tariffs — loading status...');
  statusStack.appendChild(assetsStatus);

  const syncEverythingButton = renderSyncButton({ size: 'lg' });
  const assetsButton = el('button', { class: 'btn-outline btn' }, 'Sync phone tariffs');
  let busy = false;
  assetsButton.addEventListener('click', async () => {
    if (busy || isSyncBusy()) return;
    busy = true;
    assetsButton.disabled = true;
    const originalText = assetsButton.textContent;
    assetsButton.textContent = 'Syncing phone tariffs…';
    try {
      const res = await runExclusive(() => syncBambooAssets());
      logAuditEvent({
        action: AUDIT_ACTION.SYSTEM_ASSETS_SYNC,
        details: {
          totalFetched:       res?.totalFetched ?? null,
          skippedDoneNoTariff: res?.skippedDoneNoTariff ?? null,
          updated:            res?.updated ?? null,
          withTariff:         res?.withTariff ?? null,
          durationMs:         res?.durationMs ?? null,
          errors:             res?.errors ?? 0,
        },
      });
      toast(`Updated ${res?.updated ?? 0} employees · ${res?.withTariff ?? 0} have a tariff.`, 'success');
    } catch (err) {
      if (err instanceof SyncBusyError) { toast(err.message, 'info'); }
      else {
        console.error('[settings] syncBambooAssets', err);
        toast(err.message || 'Sync failed.', 'error');
      }
    } finally {
      busy = false;
      assetsButton.disabled = isSyncBusy();
      assetsButton.textContent = originalText;
    }
  });

  // ── Individual source buttons — one per registry stage ──────────────────
  // For verifying a single source after a change without waiting on the
  // full chain. Same test-mode rule as Sync everything: these hit the live
  // external systems, so they're disabled in the sandbox.
  const singleButtons = SYNC_STAGES.map((stage) => {
    const btn = el('button', { class: 'btn-outline btn btn-sm' }, stage.buttonLabel);
    if (isTestMode()) {
      btn.disabled = true;
      btn.title = 'Disabled in test mode — syncs run against live systems, not the sandbox.';
      return btn;
    }
    btn.addEventListener('click', async () => {
      if (btn.disabled || isSyncBusy()) return;
      btn.disabled = true;
      const original = stage.buttonLabel;
      btn.textContent = 'Syncing…';
      try {
        // runStage holds the tab-wide sync lock for the source AND its
        // chained materialise, so mashing other buttons meanwhile no-ops.
        const { result, ranStages } = await runStage(stage, {
          onPhase: (phase) => { btn.textContent = phase === 'join' ? 'Joining…' : 'Syncing…'; },
        });
        // Same audit action + per-source diff shape as the full run, so the
        // audit-log renderer shows exactly the sections that ran.
        logAuditEvent({
          action: AUDIT_ACTION.SYSTEM_SYNC_RAN,
          details: { source: 'manual', stage: stage.key, ...buildSyncAuditDetails(result, ranStages) },
        });
        if (result.errors.length) {
          const msg = result.errors[0]?.error || '';
          toast(msg.includes('already running')
            ? `${stage.buttonLabel} synced. Another materialise join is running right now — it will pick these changes up.`
            : `${stage.buttonLabel} synced, but the materialise join failed — the app view may be stale. Check the console.`,
            msg.includes('already running') ? 'info' : 'error', 8000);
        } else {
          const frags = ranStages.map((x) => (result[x.key] ? x.toastFragment(result[x.key]) : null)).filter(Boolean);
          toast(`${stage.status?.label || stage.buttonLabel} synced${frags.length ? ` — ${frags.join(', ')}` : ''}.`, 'success');
        }
      } catch (err) {
        if (err instanceof SyncBusyError) { toast(err.message, 'info'); }
        else {
          console.error(`[settings] sync ${stage.key}`, err);
          toast(err.message || `${stage.buttonLabel} sync failed.`, 'error');
        }
      } finally {
        btn.disabled = isSyncBusy(); // stay grey if another sync still runs
        btn.textContent = original;
      }
    });
    return btn;
  });

  // Any sync in flight anywhere in the tab greys out every sync control
  // here — this is what makes "press four buttons at once" a no-op.
  const allSyncButtons = [...singleButtons, assetsButton];
  const unsubBusy = onSyncBusyChange((anyBusy) => {
    if (isTestMode()) return; // test-mode buttons are permanently disabled
    for (const b of allSyncButtons) {
      if (anyBusy) { b.disabled = true; b.title = 'Another sync is already running.'; }
      else if (b.textContent !== 'Syncing…' && b.textContent !== 'Joining…') { b.disabled = false; b.title = ''; }
    }
  });
  host.addEventListener('cleanup', unsubBusy, { once: true });

  card.appendChild(el('div', { class: 'divider' }));
  card.appendChild(statusStack);
  card.appendChild(el('div', { class: 'row', style: 'gap:8px' }, syncEverythingButton, assetsButton));
  card.appendChild(el('div', { class: 'label', style: 'margin-top:16px' }, 'Sync one source'));
  card.appendChild(el('div', { class: 'row', style: 'gap:8px;flex-wrap:wrap;margin-top:8px' }, ...singleButtons));
  card.appendChild(el('p', { class: 'muted', style: 'font-size:var(--fs-small);margin:8px 0 0' },
    'Source buttons re-run the Materialise join automatically, so changes show up in the app right away. The standalone Materialise button re-joins without pulling anything (it also runs pending-change reconciliation).'));

  const unsub = watchConfig('sync', (cfg) => {
    for (const stage of SYNC_STAGES) {
      const elLine = statusEls.get(stage.key);
      if (!elLine) continue;
      elLine.textContent = stage.status.line
        ? stage.status.line(cfg?.[stage.key])
        : formatStatus(stage.status.label, cfg?.[stage.key], stage.status.noun);
    }
    assetsStatus.textContent = formatAssetsStatus(cfg?.bamboo);
  });
  host.addEventListener('cleanup', unsub, { once: true }); // fires on #page, not the card

  return card;
}

// ── Slack card ─────────────────────────────────────────────────────────
//
// Holds the incoming-webhook URL Gear uses to ping Finance when an admin
// submits a device action (handover / takeback / sell / etc.). The URL is
// admin-editable so the destination can be changed without a deploy.
// `destinationLabel` is a free-text label that lands in audit rows so we
// can see where a message went in retrospect (Slack itself doesn't echo
// the channel back).
function renderSlackCard(host) {
  const urlInput = el('input', { type: 'url', placeholder: 'https://hooks.slack.com/services/T…/B…/…', style: 'flex:1' });
  const labelInput = el('input', { type: 'text', placeholder: 'e.g. #it-handovers or @finance', style: 'flex:1' });
  const enabledCheckbox = el('input', { type: 'checkbox' });
  const enabledLabel = el('label', { style: 'display:inline-flex;gap:6px;align-items:center' },
    enabledCheckbox, el('span', null, 'Send messages'));

  const saveBtn = el('button', { class: 'btn' }, 'Save');
  const testBtn = el('button', { class: 'btn-outline btn' }, 'Send test');
  const status = el('p', { class: 'muted', style: 'font-size:var(--fs-small);margin:0' }, 'Loading status...');

  let lastConfig = null;

  saveBtn.addEventListener('click', async () => {
    const payload = {
      webhookUrl:       urlInput.value.trim() || null,
      destinationLabel: labelInput.value.trim() || null,
      enabled:          enabledCheckbox.checked,
    };
    saveBtn.disabled = true;
    saveBtn.textContent = 'Saving…';
    try {
      const before = { ...lastConfig };
      await setConfig('slack', payload);
      logAuditEvent({
        action: AUDIT_ACTION.SYSTEM_CONFIG_CHANGE,
        target: { type: 'config', id: 'slack', label: 'Slack notifications' },
        details: {
          path: 'config/slack',
          before,
          after: payload,
        },
      });
      toast('Saved.', 'success');
    } catch (err) {
      console.error('[settings] save slack config', err);
      toast(err.message || 'Save failed.', 'error');
    } finally {
      saveBtn.disabled = false;
      saveBtn.textContent = 'Save';
    }
  });

  testBtn.addEventListener('click', async () => {
    testBtn.disabled = true;
    testBtn.textContent = 'Sending…';
    try {
      const res = await testSlack();
      if (res?.ok) toast('Test sent. Check Slack.', 'success');
      else toast(res?.error || res?.reason || 'Slack rejected the message.', 'error');
    } catch (err) {
      toast(err.message || 'Test failed.', 'error');
    } finally {
      testBtn.disabled = false;
      testBtn.textContent = 'Send test';
    }
  });

  const card = el('section', { class: 'card', style: 'margin-bottom:24px' },
    el('h2', { class: 'heading' }, 'Slack notifications'),
    el('p', { class: 'muted' },
      'When an admin submits a device action in Gear (hand over / take back / sell / dispose / make testing / transfer), Gear posts a message to the configured Slack destination so Finance can do their part (handover document, invoice, marking the device in Abra). Today this uses an incoming-webhook; later we’ll switch to the Herbert AI relay without changing how it’s used.'),
    el('div', { class: 'stack', style: 'gap:12px;margin-top:12px' },
      el('div', { class: 'row', style: 'gap:8px;align-items:center' },
        el('label', { class: 'label', style: 'min-width:140px' }, 'Webhook URL'),
        urlInput),
      el('div', { class: 'row', style: 'gap:8px;align-items:center' },
        el('label', { class: 'label', style: 'min-width:140px' }, 'Destination label'),
        labelInput),
      el('div', { class: 'row', style: 'gap:8px;align-items:center' },
        el('span', { class: 'label', style: 'min-width:140px' }, 'Active'),
        enabledLabel),
      status,
      el('div', { class: 'row', style: 'gap:8px;margin-top:8px' }, saveBtn, testBtn),
    ),
  );

  const unsub = watchConfig('slack', (cfg) => {
    lastConfig = cfg || {};
    urlInput.value      = cfg?.webhookUrl || '';
    labelInput.value    = cfg?.destinationLabel || '';
    enabledCheckbox.checked = cfg?.enabled !== false; // default to true when unset
    status.textContent = cfg?.webhookUrl
      ? (cfg.enabled === false
          ? 'Configured but disabled — no messages will be sent until you re-enable.'
          : `Configured. Messages go to ${cfg.destinationLabel || '(no label set)'}.`)
      : 'Not configured yet — Gear actions will be queued + audited but no Slack ping will go out.';
  });
  host.addEventListener('cleanup', unsub, { once: true }); // fires on #page, not the card

  return card;
}

function formatStatus(label, s, countLabel) {
  if (!s || !s.lastSucceededAt) return `${label} — never synced.`;
  const when  = formatDate(s.lastSucceededAt);
  const count = s.deviceCount ?? s.employeeCount ?? s.submissionCount ?? '-';
  const errors = s.summary?.errors ?? 0;
  return `${label} — last list sync ${when}, ${count} ${countLabel}${errors ? `, ${errors} errors` : ''}.`;
}

function formatAssetsStatus(bamboo) {
  if (!bamboo?.lastAssetsSucceededAt) return 'Phone tariffs — never synced manually. Run on demand below.';
  const when = formatDate(bamboo.lastAssetsSucceededAt);
  const updated = bamboo.assetsSummary?.updated ?? 0;
  const tariffs = bamboo.assetsSummary?.withTariff ?? 0;
  return `Phone tariffs — last refresh ${when}, ${updated} employees scanned, ${tariffs} active tariffs.`;
}
